/**
 * Kurzschreibweisen / Hilfsfunktionen für ThreeJS
 * @example
 * import {v3,v2,quat} from 'oc-threejs/short3';
 * const myVector = v3(1,2,3);
 */

import * as _ from "lodash";
import { Euler, EulerOrder, Quaternion, Vector2, Vector3 } from "three";

interface IVector3Like {
  x: number;
  y: number;
  z: number;
}

interface IVector2Like {
  x: number;
  y: number;
}

/**
 * Kurzschreibweise für new THREE.Vector3(x, y, z)
 */
export function v3(
  x: number | Vector3 | IVector3Like | null = 0,
  y: number = 0,
  z: number = 0,
): Vector3 {
  if (x instanceof Vector3) {
    return x.clone();
  }
  if (_.isNumber(x)) {
    return new Vector3(x, y, z);
  }
  const v = new Vector3();
  if (x) {
    // In JS: v.copy(x). Logic implies x has x,y,z.
    // ThreeJS Vector3.copy expects Vector3 (Readonly<Vector3>).
    // Pass as any to simulate JS behavior for duck typing, or cast to Vector3
    v.copy(x as Vector3);
  }
  return v;
}

/**
 * Kurzschreibweise für new THREE.Vector2(x, y)
 */
export function v2(x: number | Vector2 | IVector2Like | null = 0, y: number = 0): Vector2 {
  if (x instanceof Vector2) {
    return x.clone();
  }
  if (_.isNumber(x)) {
    return new Vector2(x, y);
  }
  const v = new Vector2();
  if (x) {
    v.copy(x as Vector2);
  }
  return v;
}

/**
 * Erzeugt ein Quaternion (x,y,z,angle) wobei angle in Grad ist.
 * Dabei steht x,y,z für eine Rotationsachse und angle für die Rotation um diese Achse
 */
export function quat(x?: number, y?: number, z?: number, angle?: number): Quaternion {
  if (x == undefined) {
    return new Quaternion();
  }
  return new Quaternion().setFromAxisAngle(new Vector3(x, y, z), (angle * Math.PI) / 180);
}

/**
 * Erzeugt ein Quaternion aus einer Euler-Rotation (x,y,z,'XYZ') wobei alle Winkel in Grad sind.
 */
export function quatEuler(
  x: number = 0,
  y: number = 0,
  z: number = 0,
  order: string = "XYZ",
): Quaternion {
  const r = Math.PI / 180;
  // THREE.Euler order string might need validation or casting to EulerOrder
  return new Quaternion().setFromEuler(new Euler(x * r, y * r, z * r, order as EulerOrder));
}
